/**
 * Giordi Web Components (handoff v2). Vanilla, no dependencies, loaded once as a module from
 * snippets/gj-styles.liquid. Each element is defined only if it is not already registered.
 *
 * <gj-wishlist-button>  heart toggle, stored in localStorage until a wishlist app is chosen
 * <gj-slider>           scroll-snap rail with prev/next buttons, progress bar and counter
 * <gj-drawer>           wraps a <dialog>: modal panel with overlay, ESC, focus trap, scroll lock
 * <gj-accordion>        groups <details>; with `single` only one stays open
 * <gj-marquee>          pauses its CSS animation while off screen
 * <gj-rotator>          rotates its children (announcement bar), one visible at a time
 * <gj-header>           mega menu (hover intent, keyboard, ESC, overlay) + --header-height for Horizon
 * <gj-submenu>          mobile menu sub-panels (slide from the right, focus, ESC = back)
 * <gj-hero>             hero slider: autoplay with progress bars and optional background videos, pauses off screen,
 *                       in hidden tabs and with its pause button
 * <gj-parallax>         shifts its content by ±max px while its section crosses the viewport
 * <gj-recommendations>  fetches its section from data-url when it nears the viewport (product recommendations)
 * <gj-faq>              FAQ category pills: shows the questions of the selected category
 * <gj-predictive-search> search overlay: live results from the Predictive Search API while typing
 * [data-gj-quick-add]   quick add button of the product cards: opens the options panel (sections/gj-quick-add)
 * [data-gj-reveal]      fade-up on scroll (IntersectionObserver, once)
 * --gj-preview-bar      height of Shopify's theme-preview bar (previews only)
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Formats cents with the shop's money format (set by gj-styles.liquid as window.Giordi.moneyFormat), so prices
 * updated in JS look exactly like the ones rendered by Liquid ("€25,00").
 * @param {number} cents
 */
const formatMoney = (cents) => {
  const format = /** @type {any} */ (window).Giordi?.moneyFormat || '€{{amount_with_comma_separator}}';
  /** @param {number} decimals @param {string} thousands @param {string} decimal */
  const number = (decimals, thousands = '.', decimal = ',') => {
    const [int = '0', dec] = (cents / 100).toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (dec ? decimal + dec : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (/** @type {string} */ _, /** @type {string} */ key) => {
    switch (key) {
      case 'amount':
        return number(2, ',', '.');
      case 'amount_no_decimals':
        return number(0, ',', '.');
      case 'amount_no_decimals_with_comma_separator':
        return number(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return number(2, "'", '.');
      default:
        return number(2, '.', ',');
    }
  });
};

/** @type {any} */ (window).Giordi = { .../** @type {any} */ (window).Giordi, formatMoney };

/** @param {string} name @param {CustomElementConstructor} element */
const define = (name, element) => {
  if (!customElements.get(name)) customElements.define(name, element);
};

/* ---------- Wishlist ---------- */

const WISHLIST_KEY = 'gj:wishlist';

/** @returns {Set<string>} */
const readWishlist = () => {
  try {
    return new Set(JSON.parse(localStorage.getItem(WISHLIST_KEY) ?? '[]'));
  } catch {
    return new Set();
  }
};

/** In-memory list: the source of truth for this page, saved to localStorage when available. */
const wishlist = readWishlist();

class GjWishlistButton extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.button?.addEventListener('click', this.#toggle);
    document.addEventListener('gj:wishlist:change', this.#sync);
    this.#sync();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#toggle);
    document.removeEventListener('gj:wishlist:change', this.#sync);
  }

  #toggle = () => {
    const id = this.dataset.productId ?? '';
    const added = !wishlist.has(id);
    if (added) wishlist.add(id);
    else wishlist.delete(id);
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify([...wishlist]));
    } catch {
      /* storage unavailable: the state lasts for this page only */
    }
    document.dispatchEvent(new CustomEvent('gj:wishlist:change', { detail: { id, added, list: [...wishlist] } }));
  };

  #sync = () => {
    this.button?.setAttribute('aria-pressed', String(wishlist.has(this.dataset.productId ?? '')));
  };
}

define('gj-wishlist-button', GjWishlistButton);

/* ---------- Slider ---------- */

/**
 * Markup: <gj-slider> with a `.gj-rail` track, optional [data-gj-prev] / [data-gj-next] buttons,
 * [data-gj-progress] (element scaled on X) and [data-gj-counter] ("2 / 8").
 */
class GjSlider extends HTMLElement {
  connectedCallback() {
    this.track = /** @type {HTMLElement | null} */ (this.querySelector('.gj-rail'));
    if (!this.track) return;
    this.prev = this.querySelector('[data-gj-prev]');
    this.next = this.querySelector('[data-gj-next]');
    this.progress = /** @type {HTMLElement | null} */ (this.querySelector('[data-gj-progress]'));
    this.counter = this.querySelector('[data-gj-counter]');

    this.prev?.addEventListener('click', this.#onPrev);
    this.next?.addEventListener('click', this.#onNext);
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(this.#update);
    this.resizeObserver.observe(this.track);
    this.#update();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.#onPrev);
    this.next?.removeEventListener('click', this.#onNext);
    this.track?.removeEventListener('scroll', this.#onScroll);
    this.resizeObserver?.disconnect();
  }

  #step() {
    const item = /** @type {HTMLElement | null} */ (this.track?.firstElementChild ?? null);
    const gap = parseFloat(getComputedStyle(/** @type {HTMLElement} */ (this.track)).columnGap) || 0;
    return item ? item.offsetWidth + gap : 0;
  }

  /** @param {number} direction */
  #scroll(direction) {
    this.track?.scrollBy({ left: direction * this.#step(), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  #onPrev = () => this.#scroll(-1);
  #onNext = () => this.#scroll(1);

  #frame = 0;
  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const track = this.track;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const ratio = max > 0 ? track.scrollLeft / max : 1;
    const visible = max > 0 ? track.clientWidth / track.scrollWidth : 1;

    // Progress: "fill" grows with the scroll (Editoriale 02); default is a thumb as wide as the visible
    // share, moving along the track (Nuovi arrivi)
    if (this.progress && this.dataset.progressMode === 'fill') {
      this.progress.style.width = `${Math.max(visible, ratio) * 100}%`;
    } else if (this.progress) {
      this.progress.style.width = `${visible * 100}%`;
      this.progress.style.transform = `translateX(${ratio * (1 / visible - 1) * 100}%)`;
    }
    if (this.prev instanceof HTMLButtonElement) this.prev.disabled = track.scrollLeft <= 1;
    if (this.next instanceof HTMLButtonElement) this.next.disabled = track.scrollLeft >= max - 1;

    if (this.counter) {
      const total = track.children.length;
      const step = this.#step();
      const index = step ? Math.min(total, Math.round(track.scrollLeft / step) + 1) : 1;
      const pad = Number(this.dataset.counterPad) || 0;
      const format = (/** @type {number} */ n) => String(n).padStart(pad, '0');
      this.counter.textContent = `${format(index)} / ${format(total)}`;
    }
  };
}

define('gj-slider', GjSlider);

/* ---------- Drawer ---------- */

/**
 * Markup: <gj-drawer id="…"><dialog class="gj-drawer__dialog">…[data-gj-close]…</dialog></gj-drawer>.
 * Open with any element [data-gj-drawer-open="<id>"] (gets aria-expanded) or `drawer.open()`.
 * The native modal <dialog> provides the focus trap, ESC and inert page; this adds the overlay
 * click, the closing animation and the body scroll lock.
 */
class GjDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.dialog.addEventListener('click', this.#onDialogClick);
    this.dialog.addEventListener('cancel', this.#onCancel);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    this.dialog?.removeEventListener('click', this.#onDialogClick);
    this.dialog?.removeEventListener('cancel', this.#onCancel);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  get triggers() {
    return document.querySelectorAll(`[data-gj-drawer-open="${this.id}"]`);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    // A classic scrollbar on the document (window under 990px with a mouse) disappears with the lock: keep its
    // width as padding so the page behind does not jump sideways
    const root = document.documentElement;
    root.style.setProperty('--gj-scrollbar-gap', `${Math.max(0, window.innerWidth - root.clientWidth)}px`);
    this.dialog.showModal();
    // showModal() focuses the first control while the panel is still off screen: browsers without overflow: clip
    // scroll the dialog to show it, and the panel would pop in instead of sliding (see gj-base.css)
    this.dialog.scrollTop = 0;
    this.dialog.scrollLeft = 0;
    root.classList.add('gj-scroll-lock');
    for (const trigger of this.triggers) trigger.setAttribute('aria-expanded', 'true');
    // Commit the closed position (off screen) before switching to [open], so the slide always starts from there
    this.querySelector('.gj-drawer__panel')?.getBoundingClientRect();
    requestAnimationFrame(() => this.setAttribute('open', ''));
    this.dispatchEvent(new CustomEvent('gj:drawer:open', { bubbles: true }));
  }

  close() {
    const dialog = this.dialog;
    if (!dialog?.open) return;
    this.removeAttribute('open');
    for (const trigger of this.triggers) trigger.setAttribute('aria-expanded', 'false');

    const finish = () => {
      dialog.close();
      // Another drawer may already be open (quick add panel → minicart): it keeps the lock
      if (!document.querySelector('gj-drawer[open]')) document.documentElement.classList.remove('gj-scroll-lock');
      this.dispatchEvent(new CustomEvent('gj:drawer:close', { bubbles: true }));
    };
    const panel = this.querySelector('.gj-drawer__panel');
    if (reducedMotion.matches || !panel) finish();
    else panel.addEventListener('transitionend', finish, { once: true });
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const trigger = target?.closest(`[data-gj-drawer-open="${this.id}"]`);
    if (trigger) {
      event.preventDefault();
      this.open();
    }
  };

  /** Clicks on the dialog backdrop (outside the panel) or on [data-gj-close] close the drawer. */
  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target === this.dialog || target?.closest('[data-gj-close]')) this.close();
  };

  /** @param {Event} event */
  #onCancel = (event) => {
    event.preventDefault();
    this.close();
  };
}

define('gj-drawer', GjDrawer);

/* ---------- Accordion ---------- */

class GjAccordion extends HTMLElement {
  connectedCallback() {
    this.addEventListener('toggle', this.#onToggle, true);
  }

  disconnectedCallback() {
    this.removeEventListener('toggle', this.#onToggle, true);
  }

  /** @param {Event} event */
  #onToggle = (event) => {
    const opened = event.target;
    if (!this.hasAttribute('single') || !(opened instanceof HTMLDetailsElement) || !opened.open) return;
    for (const details of this.querySelectorAll('details[open]')) {
      if (details !== opened) /** @type {HTMLDetailsElement} */ (details).open = false;
    }
  };
}

define('gj-accordion', GjAccordion);

/* ---------- Marquee ---------- */

class GjMarquee extends HTMLElement {
  connectedCallback() {
    this.observer = new IntersectionObserver(([entry]) => {
      this.toggleAttribute('paused', !entry?.isIntersecting);
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

define('gj-marquee', GjMarquee);

/* ---------- Rotator (announcement messages) ---------- */

class GjRotator extends HTMLElement {
  connectedCallback() {
    this.items = [...this.children];
    if (this.items.length < 2) return;
    this.index = 0;
    const seconds = Number(this.dataset.interval) || 3.5;
    this.timer = window.setInterval(this.#next, seconds * 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  #next = () => {
    if (document.hidden || !this.items) return;
    this.items[this.index]?.classList.remove('is-active');
    this.index = (this.index + 1) % this.items.length;
    this.items[this.index]?.classList.add('is-active');
  };
}

define('gj-rotator', GjRotator);

/* ---------- Header: mega menu + header height ---------- */

const desktop = window.matchMedia('(min-width: 990px)');
/** The header switches to the full-width bar (and its mega panels) here; below it the burger menu is used, nine entries do not fit before. */
const navDesktop = window.matchMedia('(min-width: 1280px)');

/** Gives the card photos of a menu panel their real src/srcset the first time the panel opens. */
const loadPanelImages = (/** @type {Element | null} */ panel) => {
  if (!panel || panel.hasAttribute('data-warm')) return;
  panel.setAttribute('data-warm', '');
  for (const img of panel.querySelectorAll('img[data-src]')) {
    if (img instanceof HTMLImageElement) {
      img.srcset = img.dataset.srcset ?? '';
      img.src = img.dataset.src ?? '';
    }
  }
};

class GjHeader extends HTMLElement {
  /** @type {HTMLElement | null} */
  #openItem = null;
  #timer = 0;

  connectedCallback() {
    this.items = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-gj-mega]')]);
    for (const item of this.querySelectorAll('.gj-nav__item')) {
      item.addEventListener('pointerenter', this.#onItemEnter);
      item.addEventListener('focusin', this.#onItemFocus);
    }
    this.addEventListener('pointerleave', this.#onLeave);
    this.addEventListener('focusout', this.#onFocusOut);
    this.addEventListener('keydown', this.#onKeydown);
    // pointermove (not pointerenter): a resting mouse under the overlay must not close a panel
    // that was opened from the keyboard
    const overlay = this.querySelector('.gj-mega-overlay');
    overlay?.addEventListener('pointermove', this.#closeNow);
    overlay?.addEventListener('click', this.#closeNow);

    this.resizeObserver = new ResizeObserver(this.#setHeight);
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    window.clearTimeout(this.#timer);
  }

  /** Horizon components (cart drawer, sticky elements) read --header-height. */
  #setHeight = () => {
    const bar = this.querySelector('.gj-header__bar');
    if (bar instanceof HTMLElement) document.body.style.setProperty('--header-height', `${bar.offsetHeight}px`);
  };

  /** @param {Event} event */
  #onItemEnter = (event) => {
    if (!navDesktop.matches || !(event.currentTarget instanceof HTMLElement)) return;
    const item = event.currentTarget;
    window.clearTimeout(this.#timer);
    // Hover intent: a short delay avoids flashing panels while the pointer crosses the nav
    this.#timer = window.setTimeout(() => this.#open(item.hasAttribute('data-gj-mega') ? item : null), 80);
  };

  /** @param {Event} event */
  #onItemFocus = (event) => {
    if (!navDesktop.matches || !(event.currentTarget instanceof HTMLElement)) return;
    const item = event.currentTarget;
    this.#open(item.hasAttribute('data-gj-mega') ? item : null);
  };

  #onLeave = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(this.#closeNow, 80);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#closeNow();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#openItem) return;
    const link = this.#openItem.querySelector('.gj-nav__link');
    this.#closeNow();
    if (link instanceof HTMLElement) link.focus();
  };

  /** @param {HTMLElement | null} item */
  #open(item) {
    if (item === this.#openItem) return;
    this.#closeNow();
    if (!item) return;
    this.#openItem = item;
    item.classList.add('is-open');
    loadPanelImages(item.querySelector('.gj-mega'));
    item.querySelector('.gj-nav__link')?.setAttribute('aria-expanded', 'true');
    this.classList.add('is-mega-open');
  }

  #closeNow = () => {
    window.clearTimeout(this.#timer);
    const item = this.#openItem;
    if (!item) return;
    item.classList.remove('is-open');
    item.querySelector('.gj-nav__link')?.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-mega-open');
    this.#openItem = null;
  };
}

define('gj-header', GjHeader);

/**
 * A click on a header (or menu drawer) link that points at the page you are already on, e.g. the logo on the home page,
 * would reload it and the browser would restore the same scroll position: scroll to the top instead, without reloading.
 */
document.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
  if (!(link instanceof HTMLAnchorElement) || !link.closest('gj-header, gj-drawer') || link.target === '_blank') return;
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || url.hash) return;
  event.preventDefault();
  /** @type {any} */ (link.closest('gj-drawer'))?.close?.();
  // On desktop Horizon scrolls inside .page-wrapper (html is overflow:hidden), on mobile the window scrolls
  const behavior = reducedMotion.matches ? 'auto' : 'smooth';
  document.querySelector('.page-wrapper')?.scrollTo({ top: 0, behavior });
  window.scrollTo({ top: 0, behavior });
});

/* ---------- Mobile menu: sub-panels ---------- */

/**
 * Markup (sections/gj-header.liquid): <gj-submenu> (display: contents) wraps the menu panel and its sub-panels
 * `.gj-menu__sub[data-gj-sub]` (inert while closed). A [data-gj-sub-open][aria-controls] button slides its sub-panel
 * in from the right and moves focus to its title; [data-gj-sub-back] or ESC goes back and returns focus to the item.
 */
class GjSubmenu extends HTMLElement {
  /** @type {HTMLElement | null} */
  #sub = null;
  /** @type {HTMLElement | null} */
  #opener = null;

  connectedCallback() {
    this.main = /** @type {HTMLElement | null} */ (this.querySelector('.gj-menu__panel'));
    this.drawer = this.closest('gj-drawer');
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.drawer?.addEventListener('gj:drawer:close', this.#reset);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.drawer?.removeEventListener('gj:drawer:close', this.#reset);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const opener = target?.closest('[data-gj-sub-open]');
    if (opener instanceof HTMLElement) this.#open(opener);
    else if (target?.closest('[data-gj-sub-back]')) this.#back();
  };

  /** ESC inside a sub-panel goes back one level instead of closing the whole menu. */
  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#sub) return;
    event.preventDefault();
    event.stopPropagation();
    this.#back();
  };

  /** @param {HTMLElement} opener */
  #open(opener) {
    const sub = document.getElementById(opener.getAttribute('aria-controls') ?? '');
    if (!sub || sub === this.#sub) return;
    this.#sub = sub;
    this.#opener = opener;
    sub.inert = false;
    sub.scrollTop = 0;
    sub.classList.add('is-open');
    loadPanelImages(sub);
    opener.setAttribute('aria-expanded', 'true');
    if (this.main) this.main.inert = true;
    const title = sub.querySelector('.gj-menu__sub-title');
    if (title instanceof HTMLElement) title.focus({ preventScroll: true });
  }

  #back() {
    const sub = this.#sub;
    const opener = this.#opener;
    if (!sub) return;
    sub.classList.remove('is-open');
    sub.inert = true;
    if (this.main) this.main.inert = false;
    opener?.setAttribute('aria-expanded', 'false');
    opener?.focus({ preventScroll: true });
    this.#sub = null;
    this.#opener = null;
  }

  /** The drawer has finished closing: start again from the first level next time (no animation, it is hidden). */
  #reset = () => {
    const sub = this.#sub;
    if (!sub) return;
    sub.classList.add('is-instant');
    this.#back();
    requestAnimationFrame(() => sub.classList.remove('is-instant'));
  };
}

define('gj-submenu', GjSubmenu);

/* ---------- Hero slider ---------- */

class GjHero extends HTMLElement {
  #index = 0;
  #timer = 0;
  /** When the current timer started and how long it still has (to resume a user pause where it stopped) */
  #started = 0;
  #remaining = 0;
  #userPaused = false;
  #hidden = false;

  connectedCallback() {
    this.slides = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.gj-hero__slide')]);
    this.bars = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.gj-hero__bar')]);
    this.videos = this.slides.map((slide) => slide.querySelector('video.gj-hero__video'));
    this.toggle = /** @type {HTMLButtonElement | null} */ (this.querySelector('.gj-hero__toggle'));
    const fallback = Number(this.dataset.interval) || 6;
    this.durations = this.slides.map((slide) => (Number(slide.dataset.duration) || fallback) * 1000);
    if (this.slides.length < 2 && !this.videos.some(Boolean)) return;

    for (const bar of this.bars) bar.addEventListener('click', this.#onBarClick);
    this.toggle?.addEventListener('click', this.#onToggle);
    for (const video of this.videos) {
      video?.addEventListener('playing', this.#onPlaying);
      video?.addEventListener('error', this.#onVideoError);
    }
    desktop.addEventListener('change', this.#onBreakpoint);
    // Off screen or in a hidden tab the slider (progress bar and video too) pauses
    this.observer = new IntersectionObserver(([entry]) => this.#setHidden(!entry?.isIntersecting || document.hidden));
    this.observer.observe(this);
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.#updateToggle();
    this.#schedule();
    this.#syncVideos();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#onVisibility);
    desktop.removeEventListener('change', this.#onBreakpoint);
  }

  get #autoplay() {
    return this.slides.length > 1 && !reducedMotion.matches && !this.hasAttribute('paused');
  }

  /** Video only without reduced motion and Save-Data: otherwise the image stays and nothing is downloaded */
  get #videoAllowed() {
    return !reducedMotion.matches && !(/** @type {any} */ (navigator).connection?.saveData);
  }

  /**
   * The source for the current viewport (mobile video below 990px, desktop video above; no cross fallback)
   * @param {HTMLVideoElement | null | undefined} video
   */
  #sourceFor(video) {
    if (!video || !this.#videoAllowed || video.dataset.failed) return '';
    return (desktop.matches ? video.dataset.srcDesktop : video.dataset.srcMobile) || '';
  }

  /** @param {number} [ms] */
  #schedule(ms = this.durations[this.#index] ?? 6000) {
    window.clearTimeout(this.#timer);
    this.#started = Date.now();
    this.#remaining = ms;
    if (this.#autoplay) this.#timer = window.setTimeout(() => this.go(this.#index + 1), ms);
  }

  #applyPause() {
    const paused = this.#userPaused || this.#hidden;
    const wasPaused = this.hasAttribute('paused');
    this.toggleAttribute('paused', paused);
    this.classList.toggle('is-user-paused', this.#userPaused);
    if (paused && !wasPaused) {
      window.clearTimeout(this.#timer);
      this.#remaining = Math.max(0, this.#remaining - (Date.now() - this.#started));
    }
    this.#syncVideos();
  }

  /** @param {boolean} hidden */
  #setHidden(hidden) {
    if (hidden === this.#hidden) return;
    this.#hidden = hidden;
    this.#applyPause();
    // Back in view: restart the current slide (bar and video) unless the user paused it
    if (!hidden && !this.#userPaused) this.go(this.#index, true);
  }

  #onVisibility = () => this.#setHidden(document.hidden);

  #onToggle = () => {
    this.#userPaused = !this.#userPaused;
    this.#applyPause();
    this.#updateToggle();
    // Resume where it stopped: the CSS bar continues, the timer gets the remaining time
    if (!this.#userPaused && !this.#hidden) this.#schedule(this.#remaining || this.durations[this.#index]);
  };

  #updateToggle() {
    const toggle = this.toggle;
    if (!toggle) return;
    toggle.hidden = !this.videos.some((video) => this.#sourceFor(video));
    const label = this.#userPaused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
    if (label) toggle.setAttribute('aria-label', label);
  }

  #onBreakpoint = () => {
    for (const video of this.videos) {
      if (!video?.getAttribute('src') || video.getAttribute('src') === this.#sourceFor(video)) continue;
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.closest('.gj-hero__slide')?.classList.remove('is-video-playing');
    }
    this.#updateToggle();
    this.#syncVideos();
  };

  /** @param {Event} event */
  #onPlaying = (event) => {
    /** @type {HTMLElement} */ (event.currentTarget).closest('.gj-hero__slide')?.classList.add('is-video-playing');
  };

  /** @param {Event} event */
  #onVideoError = (event) => {
    const video = /** @type {HTMLVideoElement} */ (event.currentTarget);
    if (!video.getAttribute('src')) return;
    // The image stays: never retry this video in this page view
    video.dataset.failed = 'true';
    video.closest('.gj-hero__slide')?.classList.remove('is-video-playing');
    this.#updateToggle();
  };

  /** Only the active slide's video plays (src set the first time it is needed); the others pause */
  #syncVideos() {
    const paused = this.hasAttribute('paused');
    this.videos.forEach((video, i) => {
      if (!video) return;
      const src = this.#sourceFor(video);
      if (i !== this.#index || !src || paused) {
        if (!video.paused) video.pause();
        return;
      }
      if (video.getAttribute('src') !== src) {
        video.muted = true;
        video.src = src;
      }
      video.play().catch(() => {
        // Autoplay refused (e.g. low-power mode): the image stays visible
      });
    });
  }

  /** @param {Event} event */
  #onBarClick = (event) => {
    const bar = /** @type {HTMLElement} */ (event.currentTarget);
    this.go(Number(bar.dataset.index));
  };

  /** @param {number} index @param {boolean} [restart] */
  go(index, restart = false) {
    const slides = this.slides ?? [];
    const next = (index + slides.length) % slides.length;
    if (next === this.#index && !restart) return this.#schedule();

    slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('aria-hidden', !active);
      for (const link of slide.querySelectorAll('a')) link.tabIndex = active ? 0 : -1;
    });
    (this.bars ?? []).forEach((bar, i) => {
      bar.classList.remove('is-active');
      bar.classList.toggle('is-done', i < next);
      bar.toggleAttribute('aria-current', i === next);
    });
    // Reflow so the progress animation restarts on the same bar
    const bar = this.bars?.[next];
    if (bar) {
      void bar.offsetWidth;
      bar.classList.add('is-active');
    }
    this.#index = next;
    this.dataset.scheme = slides[next]?.dataset.scheme || 'light';
    // The slide's video starts again from the beginning
    const video = this.videos?.[next];
    if (video?.getAttribute('src')) video.currentTime = 0;
    this.#syncVideos();
    if (this.#userPaused) this.#remaining = this.durations?.[next] ?? 6000;
    else this.#schedule();
  }
}

define('gj-hero', GjHero);

/* ---------- Parallax ---------- */

/**
 * Translates itself on Y (clamped to ±60px, ±80px from 990px) according to where its parent sits in the
 * viewport. Scroll listened in capture so it also works when Horizon's .page-wrapper is the scroller.
 */
class GjParallax extends HTMLElement {
  #frame = 0;

  connectedCallback() {
    if (reducedMotion.matches) return;
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) document.addEventListener('scroll', this.#onScroll, { passive: true, capture: true });
      else document.removeEventListener('scroll', this.#onScroll, { capture: true });
    });
    this.observer.observe(this.parentElement ?? this);
    this.#update();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('scroll', this.#onScroll, { capture: true });
    cancelAnimationFrame(this.#frame);
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#update);
  };

  #update = () => {
    const frame = this.parentElement;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    const max = desktop.matches ? 80 : 60;
    const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
    const shift = Math.max(-max, Math.min(max, -progress * max * 1.2));
    this.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0)`;
  };
}

define('gj-parallax', GjParallax);

/* ---------- Product recommendations ---------- */

class GjRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        this.observer?.disconnect();
        this.#load(url);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  /** @param {string} url */
  async #load(url) {
    try {
      const html = await fetch(url).then((response) => response.text());
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('gj-recommendations');
      // Replaces the loading skeleton; no recommendations → the section empties itself
      this.innerHTML = fresh && fresh.innerHTML.trim() ? fresh.innerHTML : '';
    } catch {
      this.innerHTML = '';
    }
  }
}

define('gj-recommendations', GjRecommendations);

/* ---------- FAQ categories ---------- */

class GjFaq extends HTMLElement {
  connectedCallback() {
    this.pills = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-gj-faq-cat]')]);
    if (this.pills.length < 2) return;
    this.addEventListener('click', this.#onClick);
    this.#show(this.pills[0]?.dataset.gjFaqCat ?? '');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const pill = event.target instanceof Element ? event.target.closest('[data-gj-faq-cat]') : null;
    if (pill instanceof HTMLElement) this.#show(pill.dataset.gjFaqCat ?? '');
  };

  /** @param {string} category */
  #show(category) {
    for (const pill of this.pills ?? []) {
      const active = pill.dataset.gjFaqCat === category;
      pill.classList.toggle('is-active', active);
      pill.setAttribute('aria-selected', String(active));
    }
    for (const item of this.querySelectorAll('[data-gj-faq-item]')) {
      const details = /** @type {HTMLDetailsElement} */ (item);
      details.hidden = details.dataset.gjFaqItem !== category;
      if (details.hidden) details.open = false;
    }
  }
}

define('gj-faq', GjFaq);

/* ---------- Predictive search ---------- */

class GjPredictiveSearch extends HTMLElement {
  #timer = 0;
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="search"]'));
    this.results = /** @type {HTMLElement | null} */ (this.querySelector('[data-gj-ps-results]'));
    this.defaultHtml = this.results?.innerHTML ?? '';
    this.input?.addEventListener('input', this.#onInput);
    this.addEventListener('click', this.#onClick);
    // Focus the field when the overlay opens
    this.closest('gj-drawer')?.addEventListener('gj:drawer:open', this.#focus);
  }

  disconnectedCallback() {
    this.input?.removeEventListener('input', this.#onInput);
    this.removeEventListener('click', this.#onClick);
    this.closest('gj-drawer')?.removeEventListener('gj:drawer:open', this.#focus);
  }

  #focus = () => window.setTimeout(() => this.input?.focus(), 50);

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const term = event.target instanceof Element ? event.target.closest('[data-gj-ps-term]') : null;
    if (!(term instanceof HTMLElement) || !this.input) return;
    this.input.value = term.dataset.gjPsTerm ?? '';
    this.input.focus();
    this.#search(this.input.value);
  };

  #onInput = () => {
    window.clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => this.#search(this.input?.value ?? ''), 250);
  };

  /** @param {string} query */
  async #search(query) {
    const results = this.results;
    if (!results) return;
    const terms = query.trim();
    this.#controller?.abort();
    if (!terms) {
      results.innerHTML = this.defaultHtml;
      return;
    }

    this.#controller = new AbortController();
    const url = new URL(this.dataset.url || '/search/suggest', window.location.origin);
    url.searchParams.set('q', terms);
    url.searchParams.set('section_id', 'gj-predictive-search');
    url.searchParams.set('resources[type]', 'product,query,collection');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[options][unavailable_products]', 'last');
    results.setAttribute('aria-busy', 'true');
    try {
      const html = await fetch(url, { signal: this.#controller.signal }).then((r) => r.text());
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-gj-ps-response]');
      if (fresh) results.replaceChildren(document.importNode(fresh, true));
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) results.innerHTML = this.defaultHtml;
    } finally {
      results.removeAttribute('aria-busy');
    }
  }
}

define('gj-predictive-search', GjPredictiveSearch);

/* ---------- Reveal on scroll ---------- */

/** Only active when gj-styles.liquid set `gj-reveal-on` (setting on, no reduced motion). */
const initReveal = () => {
  if (!document.documentElement.classList.contains('gj-reveal-on')) return;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.1 }
  );
  const observeAll = (/** @type {ParentNode} */ root) => {
    for (const element of root.querySelectorAll('[data-gj-reveal]:not(.is-visible)')) observer.observe(element);
  };
  observeAll(document);
  // Sections re-rendered by the theme editor or the Section Rendering API
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element) observeAll(event.target);
  });
};

initReveal();

/* ---------- Quick add panel ---------- */

/**
 * Card buttons [data-gj-quick-add="<product url>"] (snippets/gj-card-quick-add.liquid, products with 2+ available
 * variants) open a <gj-drawer id="GjQuickAdd">: from the right on desktop, bottom sheet on mobile (gj-product.css).
 * Its content is sections/gj-quick-add.liquid fetched with the Section Rendering API; the panel opens only once the
 * HTML, gj-product.css and gj-product.js (<gj-product>: option radios → variant) are ready, so it never slides in
 * empty or unstyled. While loading the button is aria-busy. The add itself is Horizon's product-form-component;
 * gj-cart.js closes this panel and opens the minicart once the cart has been updated. If the panel cannot be
 * loaded, the button goes to the product page.
 */
const QUICK_ADD_ID = 'GjQuickAdd';

const quickAddDrawer = () => {
  let drawer = /** @type {any} */ (document.getElementById(QUICK_ADD_ID));
  if (!drawer) {
    drawer = document.createElement('gj-drawer');
    drawer.id = QUICK_ADD_ID;
    drawer.className = 'gj-quick-drawer';
    drawer.setAttribute('side', 'right');
    drawer.innerHTML =
      '<dialog class="gj-drawer__dialog" aria-labelledby="GjQuickAddTitle"><div class="gj-drawer__panel"></div></dialog>';
    document.body.append(drawer);
  }
  return drawer;
};

/** Move the stylesheets of the fetched section into <head> (once) and wait for them. @param {Document} doc */
const loadQuickAddStyles = (doc) =>
  Promise.all(
    [...doc.querySelectorAll('link[rel="stylesheet"]')].map((link) => {
      link.remove();
      const href = new URL(link.getAttribute('href') ?? '', window.location.href).href;
      if ([...document.querySelectorAll('link[rel="stylesheet"]')].some((l) => /** @type {HTMLLinkElement} */ (l).href === href)) {
        return Promise.resolve();
      }
      const fresh = document.createElement('link');
      fresh.rel = 'stylesheet';
      fresh.href = href;
      const loaded = new Promise((resolve) => {
        fresh.addEventListener('load', resolve, { once: true });
        fresh.addEventListener('error', resolve, { once: true });
      });
      document.head.append(fresh);
      return loaded;
    })
  );

/** @type {Map<string, Promise<Document>>} */
const quickAddCache = new Map();

/** @param {string} productUrl */
const fetchQuickAdd = (productUrl) => {
  const url = new URL(productUrl, window.location.href);
  url.searchParams.set('section_id', 'gj-quick-add');
  const key = url.href;
  // Availability can change: cache only for this page view and drop it after a failure
  if (!quickAddCache.has(key)) {
    const request = fetch(key)
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
    request.catch(() => quickAddCache.delete(key));
    quickAddCache.set(key, request);
  }
  return /** @type {Promise<Document>} */ (quickAddCache.get(key)).then(
    (doc) => /** @type {Document} */ (doc.cloneNode(true))
  );
};

/** @param {HTMLElement} trigger */
const openQuickAdd = async (trigger) => {
  const productUrl = trigger.dataset.gjQuickAdd ?? '';
  if (trigger.getAttribute('aria-busy') === 'true') return;
  trigger.setAttribute('aria-busy', 'true');
  try {
    const doc = await fetchQuickAdd(productUrl);
    const content = doc.querySelector('.gj-quick');
    if (!content) throw new Error('Quick add: empty section');
    const script = content.getAttribute('data-gj-script');
    await Promise.all([loadQuickAddStyles(doc), script ? import(script) : null]);

    const drawer = quickAddDrawer();
    const panel = drawer.querySelector('.gj-drawer__panel');
    panel.replaceChildren(document.importNode(content, true));
    drawer.open();
  } catch (error) {
    console.warn('[gj-quick-add]', error);
    window.location.href = productUrl;
  } finally {
    trigger.removeAttribute('aria-busy');
  }
};

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-gj-quick-add]') : null;
  if (!(trigger instanceof HTMLElement)) return;
  event.preventDefault();
  openQuickAdd(trigger);
});

/* ---------- Shopify preview bar ---------- */

/**
 * Theme previews (?preview_theme_id=) add Shopify's bar fixed at the bottom of the screen (~68px). Its height goes into
 * --gj-preview-bar on <html>, so the product photo and the sticky add-to-cart bar fit above it. On the live shop the
 * bar does not exist and the variable stays unset (0).
 */
const initPreviewBar = () => {
  const root = document.documentElement;
  /** @param {Element} bar */
  const track = (bar) => {
    const update = () => {
      const rect = bar.getBoundingClientRect();
      const covered = rect.height > 0 && rect.bottom >= window.innerHeight - 1 ? Math.round(rect.height) : 0;
      root.style.setProperty('--gj-preview-bar', `${covered}px`);
    };
    new ResizeObserver(update).observe(bar);
    window.addEventListener('resize', update, { passive: true });
    update();
  };
  const find = () => document.getElementById('PBarNextFrameWrapper');
  const bar = find();
  if (bar) return track(bar);
  // The bar script may run after this module: watch the body briefly
  const observer = new MutationObserver(() => {
    const found = find();
    if (!found) return;
    observer.disconnect();
    track(found);
  });
  observer.observe(document.body, { childList: true });
  window.setTimeout(() => observer.disconnect(), 15000);
};

initPreviewBar();
